/**
 * @fileoverview SharedReferencePicker
 *
 * A searchable picker for resolving a reusable ("custom") attachment reference
 * to an uploaded job document. Selecting (or skipping) calls
 * `resolveJobAttachmentReference`, which writes the job-level resolution and
 * propagates it onto every precedent that uses the reference.
 *
 * Presentational + the resolve mutation only — the current `resolution` and
 * `candidates` are passed in, so the same control works in the generation
 * wizard (pre-generation), the day-one Shared Attachments dialog, and the
 * per-precedent attachment editor.
 */

import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { useConvexMutation } from "@convex-dev/react-query";
import { toast } from "sonner";
import { Check, ChevronsUpDown, Loader2 } from "lucide-react";

import { api } from "~/convex/_generated/api";
import type { Id } from "~/convex/_generated/dataModel";

import { Button } from "@/ui/primitives";
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from "@/ui/composed/overlays";
import {
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/ui/composed/menus/command";
import { cn } from "@/utils/misc";
import { extractErrorMessage } from "@/utils/errors";

export type ReferenceCandidate = {
  documentId: Id<"documents">;
  fileName: string;
};

export type ReferenceResolution = {
  status: "resolved" | "skipped";
  documentId: Id<"documents"> | null;
  fileName: string | null;
} | null;

export function SharedReferencePicker({
  jobId,
  referenceKey,
  name,
  candidates,
  resolution,
  onResolved,
  isResolved = false,
}: {
  jobId: Id<"jobs">;
  referenceKey: string;
  name: string;
  candidates: ReferenceCandidate[];
  resolution: ReferenceResolution;
  /** Optional callback after a successful resolve/skip. */
  onResolved?: () => void;
  /**
   * Whether the slot is already satisfied (a resolved shared reference or a
   * template default). When true the Skip button is hidden — skipping is only
   * meaningful while the attachment is still missing. Defaults to false so
   * other call sites keep their existing always-show-Skip behaviour.
   */
  isResolved?: boolean;
}) {
  const { mutateAsync: resolveReference, isPending } = useMutation({
    mutationFn: useConvexMutation(
      api.jobs.precedents.attachmentReferences.resolveJobAttachmentReference,
    ),
  });
  const [open, setOpen] = useState(false);

  const resolvedDocId = resolution?.documentId ?? null;
  const selected = candidates.find((c) => c.documentId === resolvedDocId);
  const isSkipped = resolution?.status === "skipped";
  const triggerLabel =
    selected?.fileName ??
    resolution?.fileName ??
    (candidates.length > 0
      ? "Select an uploaded job document"
      : "No job documents uploaded");

  const pick = async (documentId: Id<"documents">) => {
    setOpen(false);
    try {
      await resolveReference({ jobId, referenceKey, name, documentId });
      toast.success("Applied to every precedent using this reference");
      onResolved?.();
    } catch (err) {
      toast.error(extractErrorMessage(err, "Failed to apply attachment"));
    }
  };

  const skip = async () => {
    try {
      await resolveReference({ jobId, referenceKey, name, skip: true });
      toast.success("Reference skipped");
      onResolved?.();
    } catch (err) {
      toast.error(extractErrorMessage(err, "Failed to skip reference"));
    }
  };

  return (
    <div className="space-y-1.5">
      <div
        className={cn(
          "grid gap-2",
          isResolved ? "md:grid-cols-1" : "md:grid-cols-[1fr_auto]",
        )}
      >
        <Popover open={open} onOpenChange={setOpen} modal>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              role="combobox"
              disabled={isPending}
              className="w-full justify-between overflow-hidden text-sm font-normal"
            >
              <span className="flex min-w-0 items-center gap-1.5 text-left">
                {resolvedDocId && (
                  <Check className="h-3.5 w-3.5 shrink-0 text-emerald-600 dark:text-emerald-400" />
                )}
                <span className="truncate" title={triggerLabel}>
                  {triggerLabel}
                </span>
              </span>
              {isPending ? (
                <Loader2 className="ml-2 h-3.5 w-3.5 shrink-0 animate-spin opacity-50" />
              ) : (
                <ChevronsUpDown className="ml-2 h-3.5 w-3.5 shrink-0 opacity-50" />
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent
            className="w-[var(--radix-popover-trigger-width)] p-0"
            align="start"
            onWheel={(e) => e.stopPropagation()}
          >
            <Command onKeyDown={(e) => e.stopPropagation()}>
              <CommandInput placeholder="Search job documents..." />
              <CommandList className="max-h-64 overscroll-contain">
                <CommandEmpty>No matching document.</CommandEmpty>
                {candidates.map((candidate) => (
                  <CommandItem
                    key={candidate.documentId}
                    value={candidate.fileName}
                    onSelect={() => pick(candidate.documentId)}
                  >
                    <Check
                      className={cn(
                        "mr-2 h-3 w-3 shrink-0",
                        resolvedDocId === candidate.documentId
                          ? "opacity-100"
                          : "opacity-0",
                      )}
                    />
                    <span className="truncate" title={candidate.fileName}>
                      {candidate.fileName}
                    </span>
                  </CommandItem>
                ))}
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>

        {!isResolved && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={isPending}
            onClick={skip}
          >
            Skip
          </Button>
        )}
      </div>
      {isSkipped && (
        <p className="text-xs text-primary/45">
          Skipped — select a document above to restore it.
        </p>
      )}
    </div>
  );
}
